import { FC } from 'react';
import { Button, Divider, Modal, Typography } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useAlert, useAlertsCatalog } from '@/queries';
import { AlertIcon, CreateAlertIcon, EmptySection, SkeletonList } from '@/shared';
import InlineIconLabel from '@/shared/components/modals/ResourceCreationModals/CreateOAuthApplicationModal/components/InlineIconLabel';
import { ACCOUNT_NOTIFICATION_SETTINGS_PATHS } from '@/shared/constants';
import { WorkspaceAlert } from '@/types';

import AlertEditorModal from '../../components/AlertEditorModal';
import AlertManagementList, { AlertManagementSurface } from '../../components/AlertManagementList';

interface AlertRouteState {
    detailSurface?: AlertManagementSurface;
}

const AlertsView: FC = (): JSX.Element => {
    const { token } = useAntDesignTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const catalogQuery = useAlertsCatalog();
    const alertResourceSegment = location.pathname
        .slice(ACCOUNT_NOTIFICATION_SETTINGS_PATHS.alerts.length)
        .split('/')
        .filter(Boolean)[0];
    const creating = alertResourceSegment === 'new';
    const selectedAlertId = creating ? undefined : alertResourceSegment;
    const selectedAlertQuery = useAlert(selectedAlertId);
    const editorOpen = Boolean(alertResourceSegment);
    const routeState = location.state as AlertRouteState | null;

    const closeEditor = () => navigate(ACCOUNT_NOTIFICATION_SETTINGS_PATHS.alerts, { replace: true });
    const openCreateEditor = () => navigate(ACCOUNT_NOTIFICATION_SETTINGS_PATHS.createAlert);
    const openAlertEditor = (alert: WorkspaceAlert, detailSurface: AlertManagementSurface = 'properties') =>
        navigate(ACCOUNT_NOTIFICATION_SETTINGS_PATHS.alert(alert.id), {
            state: { detailSurface } satisfies AlertRouteState,
        });

    return (
        <div data-notifications-alerts-content='true' style={{ paddingTop: '1rem' }}>
            <Typography.Title level={4}>
                <InlineIconLabel icon={<AlertIcon />} label='Alerts' />
            </Typography.Title>
            <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                Create permission-aware, event-driven monitors for institutional research and due-diligence workflows.
            </Typography.Paragraph>
            <Divider style={{ margin: '1rem 0' }} />
            {catalogQuery.isLoading ? (
                <SkeletonList listLength={6} maxHeight='25rem' minHeight='25rem' />
            ) : catalogQuery.isError || !catalogQuery.data ? (
                <EmptySection
                    description='The alert catalog could not be loaded. Try again or contact support if the problem persists.'
                    extra={
                        <Button size='small' onClick={() => void catalogQuery.refetch()}>
                            Try Again
                        </Button>
                    }
                    height='25rem'
                    icon={<AlertIcon />}
                    label='Alerts Unavailable'
                />
            ) : (
                <AlertManagementList
                    action={
                        <Button type='primary' icon={<CreateAlertIcon />} onClick={openCreateEditor}>
                            Create Alert
                        </Button>
                    }
                    catalog={catalogQuery.data}
                    selectedAlertId={selectedAlertId}
                    onOpen={openAlertEditor}
                />
            )}
            {editorOpen && catalogQuery.data && (creating || selectedAlertQuery.data) ? (
                <AlertEditorModal
                    key={alertResourceSegment}
                    catalog={catalogQuery.data}
                    initialAlert={selectedAlertQuery.data}
                    initialManageSurface={routeState?.detailSurface}
                    initialView={creating ? 'create' : 'manage'}
                    open
                    onClose={closeEditor}
                    onDeleted={(deletedAlert) => {
                        if (deletedAlert.id === selectedAlertId) {
                            closeEditor();
                        }
                    }}
                    onSaved={(savedAlert) =>
                        navigate(ACCOUNT_NOTIFICATION_SETTINGS_PATHS.alert(savedAlert.id), {
                            replace: true,
                            state: { detailSurface: 'properties' } satisfies AlertRouteState,
                        })
                    }
                    onSelectAlert={openAlertEditor}
                />
            ) : null}
            {editorOpen && !creating && catalogQuery.data && selectedAlertQuery.isPending ? (
                <Modal open footer={null} title='Loading Alert' onCancel={closeEditor}>
                    <SkeletonList listLength={4} minHeight='16rem' />
                </Modal>
            ) : null}
            {editorOpen && !creating && catalogQuery.data && selectedAlertQuery.isError ? (
                <Modal open footer={null} title='Alert Unavailable' onCancel={closeEditor}>
                    <EmptySection
                        description='This alert does not exist or is not available in your active workspace.'
                        extra={
                            <Button type='primary' onClick={closeEditor}>
                                Return To Alerts
                            </Button>
                        }
                        height='16rem'
                        icon={<AlertIcon />}
                        label='Alert Not Found'
                    />
                </Modal>
            ) : null}
        </div>
    );
};

export default AlertsView;
